import { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, ChevronDown } from "lucide-react";
import { faqService } from "@/services/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmationModal } from "@/components/shared/ConfirmationModal";
import toast from "react-hot-toast";
import type { FAQ } from "@/types";

export function FAQAdminPage() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [editing, setEditing] = useState<FAQ | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<FAQ | null>(null);

  useEffect(() => {
    faqService.getAll().then(setFaqs);
  }, []);

  const handleSave = async () => {
    if (!editing) return;
    try {
      if (editing.id && faqs.find((f) => f.id === editing.id)) {
        await faqService.update(editing.id, editing);
        toast.success("FAQ mise à jour");
      } else {
        await faqService.create(editing);
        toast.success("FAQ créée");
      }
      setEditing(null);
      setIsCreating(false);
      faqService.getAll().then(setFaqs);
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = (faq: FAQ) => {
    setFaqToDelete(faq);
  };

  const confirmDelete = async () => {
    if (!faqToDelete) return;
    try {
      await faqService.delete(faqToDelete.id);
      toast.success("FAQ supprimée");
      setFaqs((prev) => prev.filter((f) => f.id !== faqToDelete.id));
      setFaqToDelete(null);
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const newFAQ: FAQ = {
    id: "",
    question: "",
    answer: "",
    category: "Général",
    order: faqs.length + 1,
    active: true,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark">Gestion des FAQ</h1>
          <p className="text-gray-500 text-sm">{faqs.length} questions</p>
        </div>
        <Button
          onClick={() => {
            setEditing(newFAQ);
            setIsCreating(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </div>

      {(editing || isCreating) && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <h3 className="font-bold text-lg">
              {isCreating ? "Nouvelle question" : "Modifier la question"}
            </h3>
            <Input
              label="Question"
              value={editing?.question || ""}
              onChange={(e) => setEditing((prev) => prev ? { ...prev, question: e.target.value } : null)}
            />
            <Textarea
              label="Réponse"
              value={editing?.answer || ""}
              onChange={(e) => setEditing((prev) => prev ? { ...prev, answer: e.target.value } : null)}
              rows={4}
            />
            <Input
              label="Catégorie"
              value={editing?.category || ""}
              onChange={(e) => setEditing((prev) => prev ? { ...prev, category: e.target.value } : null)}
            />
            <div className="flex gap-3">
              <Button onClick={handleSave}>Sauvegarder</Button>
              <Button variant="outline" onClick={() => { setEditing(null); setIsCreating(false); }}>
                Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {faqs.map((faq) => (
          <Card key={faq.id}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-dark mb-2">{faq.question}</h4>
                  <p className="text-gray-500 text-sm">{faq.answer}</p>
                  <span className="inline-block mt-2 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-500">
                    {faq.category}
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => { setEditing(faq); setIsCreating(false); }}
                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(faq)}
                    className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <ConfirmationModal
        isOpen={!!faqToDelete}
        onClose={() => setFaqToDelete(null)}
        onConfirm={confirmDelete}
        title="Supprimer la FAQ"
        message="Êtes-vous sûr de vouloir supprimer cette question ?"
      />
    </div>
  );
}
